import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, FileText, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';

const API = process.env.REACT_APP_BACKEND_URL;

const CHARTER_SECTIONS = [
  {
    title: "Ce que le Mentor PEUT faire",
    icon: CheckCircle,
    color: "#06d6a0",
    items: [
      "Partager son expérience personnelle et professionnelle",
      "Guider et orienter l'apprenant dans son parcours d'apprentissage",
      "Suggérer des ressources, des formations et des outils",
      "Animer des sessions de pratique sur des compétences non réglementées",
      "Donner son avis sur des projets, portfolios ou productions",
      "Proposer un accompagnement en développement personnel non thérapeutique",
    ],
  },
  {
    title: "Ce que le Mentor NE PEUT PAS faire",
    icon: AlertTriangle,
    color: "#ff6b6b",
    items: [
      "Fournir des conseils médicaux, psychologiques, juridiques ou financiers professionnels",
      "Contacter des membres en dehors de la plateforme Kurso sans consentement explicite",
      "Partager ou utiliser les données personnelles des apprenants à des fins commerciales",
      "Solliciter des paiements en dehors de la plateforme Kurso",
      "Se présenter comme professionnel certifié dans un domaine réglementé sans l'être",
      "Exercer une pression ou un abus d'influence sur les apprenants",
    ],
  },
  {
    title: "Code de conduite",
    icon: Shield,
    color: "#a78bfa",
    items: [
      "Respecter la confidentialité des échanges avec les apprenants",
      "Maintenir une attitude professionnelle et bienveillante",
      "Déclarer tout conflit d'intérêts potentiel",
      "Signaler à Kurso tout comportement abusif ou inapproprié d'un apprenant",
      "Maintenir à jour votre profil et vos disponibilités",
      "Répondre aux apprenants dans un délai raisonnable (48h maximum)",
    ],
  },
  {
    title: "Responsabilités et limite de responsabilité",
    icon: FileText,
    color: "#ffd93d",
    items: [
      "Le Mentor est responsable de l'exactitude des informations figurant sur son profil",
      "Kurso agit uniquement comme plateforme de mise en relation — Kurso n'est pas responsable du contenu des échanges",
      "Le Mentor conserve l'entière responsabilité des conseils qu'il prodigue",
      "En cas de litige, Kurso peut suspendre l'accès du Mentor en attente d'investigation",
      "Kurso se réserve le droit de retirer le statut Mentor à tout moment sans préavis en cas de violation",
    ],
  },
];

export default function CharteMentor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  const handleAccept = async () => {
    if (!checked) { toast.error('Veuillez cocher la case de confirmation'); return; }
    if (!user) { navigate('/auth'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/mentors/me/accept-charter`, {}, { withCredentials: true });
      setAccepted(true);
      toast.success('Charte acceptée ! Votre profil mentor est maintenant actif.');
      setTimeout(() => navigate('/profile'), 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Charte Mentor — Kurso</title>
        <meta name="description" content="Charte d'utilisation pour les mentors Kurso. Droits, obligations et code de conduite." />
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="mb-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#ff6b6b]/20 flex items-center justify-center mx-auto mb-4">
            <Shield size={24} className="text-[#ff6b6b]" />
          </div>
          <h1 className="text-4xl font-heading font-bold text-white mb-3">Charte Mentor Kurso</h1>
          <p className="text-[#a0a0ab]">En devenant mentor sur Kurso, vous acceptez les conditions suivantes. Veuillez les lire attentivement.</p>
        </div>

        {/* Warning */}
        <div className="flex items-start gap-2 bg-[#ffd93d]/10 border border-[#ffd93d]/20 rounded-xl p-4 mb-6 text-sm text-[#ffd93d]">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <p>Les mentors Kurso sont des facilitateurs bénévoles ou indépendants. Leurs conseils ne remplacent en aucun cas l'avis d'un professionnel qualifié (médecin, juriste, psychologue, thérapeute, comptable, conseiller financier, ou tout autre expert certifié dans son domaine). Kurso agit uniquement comme plateforme de mise en relation et décline toute responsabilité quant aux conseils prodigués par les mentors.</p>
        </div>

        <div className="space-y-4 mb-8">
          {CHARTER_SECTIONS.map(section => {
            const Icon = section.icon;
            return (
              <div key={section.title} className="kurso-card p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={16} style={{ color: section.color }} />
                  <h2 className="text-white font-bold text-sm">{section.title}</h2>
                </div>
                <ul className="space-y-1.5">
                  {section.items.map(item => (
                    <li key={item} className="flex items-start gap-2 text-[#a0a0ab] text-sm">
                      <span style={{ color: section.color }} className="flex-shrink-0 mt-0.5">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Renewal note */}
        <p className="text-xs text-[#a0a0ab] text-center mb-6">Cette charte doit être réacceptée annuellement. La date d'acceptation est enregistrée dans notre base de données.</p>

        {/* Acceptance */}
        {user ? (
          accepted ? (
            <div className="text-center py-6">
              <CheckCircle size={40} className="text-[#06d6a0] mx-auto mb-3" />
              <p className="text-white font-bold">Charte acceptée !</p>
              <p className="text-[#a0a0ab] text-sm">Redirection vers votre profil...</p>
            </div>
          ) : (
            <div className="kurso-card p-5">
              <label className="flex items-start gap-3 cursor-pointer mb-4">
                <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)}
                  data-testid="charter-accept-checkbox"
                  className="mt-1 accent-[#ff6b6b]" />
                <span className="text-[#e8e8e8] text-sm">
                  J'ai lu et j'accepte la Charte Mentor Kurso dans son intégralité. Je comprends mes droits, mes obligations et les limites de responsabilité.
                </span>
              </label>
              <button onClick={handleAccept} disabled={loading || !checked}
                data-testid="charter-accept-btn"
                className="w-full py-3 rounded-xl bg-[#ff6b6b] text-white font-bold hover:bg-[#ff5555] transition-colors disabled:opacity-40">
                {loading ? 'Enregistrement...' : 'Accepter la Charte Mentor'}
              </button>
            </div>
          )
        ) : (
          <div className="text-center">
            <p className="text-[#a0a0ab] text-sm mb-3">Connectez-vous pour accepter la charte et activer votre statut mentor.</p>
            <a href="/auth" className="inline-block bg-[#ff6b6b] text-white px-6 py-2.5 rounded-xl font-semibold text-sm">Se connecter</a>
          </div>
        )}
      </div>
    </Layout>
  );
}
